import { useQueryClient } from '@tanstack/react-query';
import * as R from 'ramda';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import storage from '../../hooks/useStorage/useStorage';
import LangEnum from '../../lang/LangEnum';
import { TeceoApiResponse } from '../../lib/basicRepository';
import { useApplicationContext } from '../../modules/context/ApplicationContext/ApplicationContext';
import useOrderBook from '../../modules/order-book/hooks/useOrderBook';
import { OrderView } from '../../modules/orders/ordersView.types';
import { productActionsRepository } from '../../modules/products/repositories/productActions/productActions.repository';
import {
  ShopByOutcome,
  ShopByResultOutput,
} from '../../modules/products/repositories/types/productActions.types';
import { getTotalProductColorSelloutKey } from '../../services/modules/product-color-sellout/product-color-sellout.keys';
import { OrderBookOrder } from '../../stores/OrderBook/orderBook.types';
import useProductActionsStore from '../../stores/ProductActionsStore/productActions.store';
import useProductDetailsStore from '../../stores/ProductDetails/productDetails.store';
import { sleep } from '../../utils/generics';
import useWidth from '../Hooks/useWidth';
import { SaleItemType } from './interfaces/product-action.dto';
import { SearchOption } from './interfaces/search-option.enum';
import { SearchStatus } from './interfaces/search-status.enum';
import {
  OnSearchProduct,
  RepositoryAction,
  SearchProductParams,
  UpdateOrderRelationsParams,
  UseSearchProduct,
} from './interfaces/use-search-product.interface';

const repositoryActions = {
  [SearchOption.ADD]: async (
    code: string,
    selectedOrder: OrderBookOrder,
  ): Promise<TeceoApiResponse<ShopByResultOutput>> => {
    const response = await productActionsRepository().shopByProduct(selectedOrder.id, code);

    return response;
  },
  [SearchOption.SEARCH]: async ({
    code,
    isAdminSeller,
    catalogId,
  }: SearchProductParams): RepositoryAction => {
    const response = await productActionsRepository().searchProduct({
      codeOrBarcodeOrName: code,
      isAdminSeller,
      catalogId,
    });

    if (response.ok && response.data && 'productId' in response.data) {
      return {
        productColorId: response.data.productColorId,
        productId: response.data.productId,
        isAlreadyShopping: false,
      };
    } else if (response.ok && response.data && 'kitId' in response.data) {
      return {
        kitId: response.data.kitId,
        kitCode: response.data.kitCode,
        kitName: response.data.kitName,
      };
    }
  },
  [SearchOption.CAMERA]: (): RepositoryAction | undefined => undefined,
  [SearchOption.NONE]: (): RepositoryAction | undefined => undefined,
};

const useSearchProduct = (): UseSearchProduct => {
  const history = useHistory();
  const { mobileWidth } = useWidth();
  const queryClient = useQueryClient();
  const { team } = useApplicationContext();

  const { setProductDetailsModalState, handleOpenProductDetailsModal } = useProductDetailsStore(
    useShallow((productDetailsState) => ({
      setProductDetailsModalState: productDetailsState.setProductDetailsModalState,
      handleOpenProductDetailsModal: productDetailsState.handleOpenProductDetailsModal,
    })),
  );

  const { openProductNotification, setOrderProducts, selectedOption, setSelectedOption } =
    useProductActionsStore(
      useShallow((state) => ({
        openProductNotification: state.openProductNotification,
        setOrderProducts: state.setOrderProducts,
        selectedOption: state.selectedOption,
        setSelectedOption: state.setSelectedOption,
      })),
    );

  const [inputCode, setInputCode] = useState('');
  const { openOrderBook, allOrders } = useOrderBook();
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<SearchStatus>(SearchStatus.NONE);
  const [errorMessage, setErrorMessage] = useState<LangEnum | undefined>();

  const searchProduct = async ({
    code,
    isAdminSeller,
    catalogId,
  }: SearchProductParams): RepositoryAction => {
    setIsLoading(true);

    const product = await repositoryActions[SearchOption.SEARCH]({
      code,
      isAdminSeller,
      catalogId,
    });
    setIsLoading(false);

    if (!product) {
      return;
    }

    if ('productColorId' in product && product.productColorId && product.productId) {
      return {
        productColorId: product?.productColorId,
        productId: product?.productId,
        isAlreadyShopping: false,
      };
    } else if ('kitId' in product && product.kitId && 'kitCode' in product) {
      return {
        kitId: product?.kitId,
        kitCode: product?.kitCode,
        kitName: product?.kitName,
      };
    }
  };

  const searchForAddOption = async (
    code: string,
    order: OrderBookOrder,
  ): Promise<TeceoApiResponse<ShopByResultOutput>> => {
    setIsLoading(true);
    const response = await repositoryActions[SearchOption.ADD](code, order);
    setIsLoading(false);

    return response;
  };

  const validate = (option: SearchOption, order?: OrderBookOrder) => {
    switch (option) {
      case SearchOption.ADD: {
        if (R.isNil(order)) {
          openOrderBook();
          return false;
        }
        return true;
      }

      default:
        return true;
    }
  };

  const updateOrderRelations = ({
    option,
    orderId,
    reloadOrderItemsQuantity,
  }: UpdateOrderRelationsParams) => {
    switch (option) {
      case SearchOption.ADD: {
        reloadOrderItemsQuantity(false);
        orderId &&
          queryClient.invalidateQueries({
            queryKey: [team?.id, ...getTotalProductColorSelloutKey({ orderId })],
          });
        break;
      }

      default:
        break;
    }
  };

  const redirectToOrderEdit = (orderId: string) => {
    history.push(`/orders/${orderId}/${OrderView.EDIT}`);
    openProductNotification({ productAdd: true, productAlready: false });
  };

  const handleAddOption = async ({
    option,
    code,
    reloadOrderItemsQuantity,
    order,
  }: OnSearchProduct) => {
    if (!order || R.isEmpty(code) || !validate(option, order)) {
      return;
    }

    const response = await searchForAddOption(code, order);

    if (!response.ok) {
      const errorCode = response?.data?.code;
      setErrorMessage(errorCode ? errorCode : undefined);
    }

    if (!response.ok || !response.data) {
      setStatus(SearchStatus.ERROR);
      return SearchStatus.ERROR;
    }

    const data = response.data;
    updateOrderRelations({ option, orderId: order.id, reloadOrderItemsQuantity });
    setInputCode('');
    setStatus(SearchStatus.SUCCESS);

    if (data.outcome !== ShopByOutcome.ALREADY_IN_ORDER) {
      setOrderProducts(data.orderProducts ?? []);
      redirectToOrderEdit(order.id);
      return;
    }

    const isKit = data.saleItemType === SaleItemType.KIT;

    openProductNotification({
      productAdd: false,
      productAlready: true,
      productColorId: isKit ? undefined : data.productColorId,
      kitId: isKit ? data.kitId : undefined,
    });

    setStatus(SearchStatus.SUCCESS);
  };

  const handleSearch = async ({
    option,
    code,
    reloadOrderItemsQuantity,
    order,
    catalogId,
    isAdminSeller,
  }: OnSearchProduct) => {
    if (R.isEmpty(code) || !validate(option, order)) {
      return;
    }

    const productOrKit = await searchProduct({
      code,
      isAdminSeller,
      catalogId,
    });

    if (!productOrKit) {
      setStatus(SearchStatus.ERROR);
      return SearchStatus.ERROR;
    }

    updateOrderRelations({ option, reloadOrderItemsQuantity });
    setInputCode('');
    setStatus(SearchStatus.SUCCESS);

    const productDetailsFilterSelectedByUser = storage.getProductDetailsFilter(
      storage.getSessionId() as string,
    );
    const isAllColorsFilterSelectedByUser = productDetailsFilterSelectedByUser?.applyAllColors;
    const isAllOrdersFilterSelectedByUser = productDetailsFilterSelectedByUser?.applyAllOrders;
    const isRelatedProductsFilterSelectedByUser =
      productDetailsFilterSelectedByUser?.applyRelatedProducts;
    const availabilityFilterSelectedByUser = productDetailsFilterSelectedByUser?.availabilityFilter;

    let orderIds: string[] | [] = order?.id ? [order.id] : [];
    if (isAllOrdersFilterSelectedByUser && !mobileWidth) {
      orderIds = allOrders.map((o) => o.id);
    }

    if ('kitId' in productOrKit && productOrKit.kitId && 'kitCode' in productOrKit) {
      handleOpenProductDetailsModal({ orderIds }, isRelatedProductsFilterSelectedByUser);
      setProductDetailsModalState({
        isOpen: true,
        kitId: productOrKit.kitId,
      });
    } else if ('productId' in productOrKit && productOrKit.productId) {
      handleOpenProductDetailsModal(
        {
          productColorId: isAllColorsFilterSelectedByUser ? undefined : productOrKit.productColorId,
          orderIds,
          isFinishedOrder: false,
          availability: availabilityFilterSelectedByUser,
        },
        isRelatedProductsFilterSelectedByUser,
      );
      setProductDetailsModalState({
        isOpen: true,
        productId: productOrKit.productId,
        productColorId: productOrKit.productColorId,
      });
    }
  };

  const resetStatus = useCallback(async () => {
    await sleep(3000);
    setErrorMessage(undefined);
    setStatus(SearchStatus.NONE);
  }, []);

  useEffect(() => {
    resetStatus();
  }, [resetStatus, status]);

  return {
    status,
    inputCode,
    isLoading,
    errorMessage,
    selectedOption,
    onChangeSelectedOption: setSelectedOption,
    onSearch: handleSearch,
    onAdd: handleAddOption,
    onChangeInputCode: setInputCode,
  };
};

export default useSearchProduct;
